import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2, Sparkles, Users2, Download, Trash2, Target, TrendingUp, Megaphone, Lightbulb, BarChart3 } from "lucide-react";
import { Button } from "../ui/button";
import AudienceROIPanel from "./AudienceROIPanel";

/**
 * GoogleAudiencesTab — onglet "Audiences IA" du module Marketing Google.
 *
 * Workflow :
 *   1. Preview : stats agrégées des prospects HOT/WARM (sans appel IA, instantané).
 *   2. Generate : appel Gemini → brief publicitaire complet (persona, mots-clés, ad copy, budget, ROAS).
 *   3. Liste des briefs générés + actions (export CSV pour Meta/Google Ads, suppression).
 */
const eur = (n) =>
  Number(n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const TIER_COLOR = { hot: "#f43f5e", warm: "#f59e0b", cold: "#0ea5e9" };

export default function GoogleAudiencesTab() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audiences, setAudiences] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [criteria, setCriteria] = useState({ tiers: ["hot", "warm"], min_score: 50 });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [prev, list] = await Promise.all([
        api.get(`/google/audiences/preview?tiers=${criteria.tiers.join(",")}&min_score=${criteria.min_score}`),
        api.get("/google/audiences"),
      ]);
      setPreview(prev.data);
      setAudiences(list.data || []);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria.tiers.join(","), criteria.min_score]);

  useEffect(() => {
    reload();
  }, [reload]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/google/audiences/generate", criteria);
      setSelected(r.data);
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Échec de génération");
    } finally {
      setGenerating(false);
    }
  };

  const exportCsv = async (audId) => {
    try {
      const r = await api.get(`/google/audiences/${audId}/csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `audience_${audId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Erreur export CSV");
    }
  };

  const remove = async (audId) => {
    if (!window.confirm("Supprimer cette audience ?")) return;
    await api.delete(`/google/audiences/${audId}`);
    if (selected?.audience_id === audId) setSelected(null);
    reload();
  };

  const toggleTier = (t) => {
    setCriteria((c) => ({
      ...c,
      tiers: c.tiers.includes(t) ? c.tiers.filter((x) => x !== t) : [...c.tiers, t],
    }));
  };

  return (
    <div className="space-y-6" data-testid="google-audiences-tab">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#f7c935]/10 via-[#14101c] to-[#0b0811] border border-[#f7c935]/20 rounded-2xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#f7c935] flex items-center justify-center flex-shrink-0">
            <Megaphone className="w-6 h-6 text-[#14101c]" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-white">Audiences publicitaires IA</h3>
            <p className="text-sm text-slate-400 mt-1">
              Convertissez vos prospects HOT/WARM en <strong>audiences lookalike Meta & Google Ads</strong> en 1 clic.
              L'IA génère le persona, les mots-clés, l'ad copy et le budget recommandé d'après vos signaux d'achat réels.
            </p>
          </div>
        </div>
      </div>

      {/* Critères + Preview */}
      <div className="bg-[#14101c] border border-white/5 rounded-2xl p-5">
        <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <Target className="w-4 h-4 text-[#f7c935]" /> Critères de sélection
        </h4>
        <div className="flex items-center gap-4 flex-wrap mb-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">Tiers IA</label>
            <div className="flex gap-1.5">
              {["hot", "warm", "cold"].map((t) => (
                <button
                  key={t}
                  onClick={() => toggleTier(t)}
                  className={`text-xs px-3 py-1.5 rounded-lg font-bold uppercase transition-colors ${criteria.tiers.includes(t) ? "ring-2" : "opacity-50"}`}
                  style={{ background: TIER_COLOR[t] + "22", color: TIER_COLOR[t], boxShadow: criteria.tiers.includes(t) ? `inset 0 0 0 1px ${TIER_COLOR[t]}` : "none" }}
                  data-testid={`audience-tier-${t}`}
                >
                  {t === "hot" ? "🔥 Chaud" : t === "warm" ? "🌤️ Tiède" : "❄️ Froid"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">Score min.</label>
            <input
              type="number"
              min={0}
              max={100}
              value={criteria.min_score}
              onChange={(e) => setCriteria((c) => ({ ...c, min_score: parseInt(e.target.value || "0", 10) }))}
              className="bg-[#0b0811] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white w-24"
              data-testid="audience-min-score"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Calcul…</div>
        ) : preview?.empty ? (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-300" data-testid="audience-empty">
            Aucun prospect ne correspond. Lancez d'abord des évaluations IA depuis vos fiches prospects.
          </div>
        ) : (
          <>
            {/* Preview KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5" data-testid="audience-preview">
              <div className="bg-[#0b0811] rounded-lg border border-white/5 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Audience</div>
                <div className="text-xl font-bold text-white">{preview.total}</div>
                <div className="text-[10px] text-slate-500">prospects</div>
              </div>
              <div className="bg-[#0b0811] rounded-lg border border-white/5 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Score moyen</div>
                <div className="text-xl font-bold text-[#f7c935]">{preview.stats?.avg_score}/100</div>
              </div>
              <div className="bg-[#0b0811] rounded-lg border border-white/5 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Mix tiers</div>
                <div className="text-xs font-bold text-white mt-1 space-x-1">
                  {Object.entries(preview.stats?.tiers || {}).map(([t, n]) => (
                    <span key={t} style={{ color: TIER_COLOR[t] }}>{n}{t[0].toUpperCase()}</span>
                  ))}
                </div>
              </div>
              <div className="bg-[#0b0811] rounded-lg border border-white/5 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Top module</div>
                <div className="text-sm font-bold text-white truncate" title={preview.stats?.top_modules?.[0]?.[0] || "—"}>
                  {preview.stats?.top_modules?.[0]?.[0] || "—"}
                </div>
                <div className="text-[10px] text-slate-500">{preview.stats?.top_modules?.[0]?.[1] || 0} prospects</div>
              </div>
              <div className="bg-[#0b0811] rounded-lg border border-white/5 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Top statut</div>
                <div className="text-sm font-bold text-white truncate">{preview.stats?.top_statuses?.[0]?.[0] || "—"}</div>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                onClick={generate}
                disabled={generating || preview.total < 3}
                className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold"
                data-testid="audience-generate-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Générer un brief IA
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Briefs list */}
      {audiences.length > 0 && (
        <div className="bg-[#14101c] border border-white/5 rounded-2xl p-5">
          <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <Users2 className="w-4 h-4 text-[#f7c935]" /> Briefs générés ({audiences.length})
          </h4>
          <div className="space-y-2" data-testid="audience-list">
            {audiences.map((a) => (
              <div key={a.audience_id} className="bg-[#0b0811] rounded-xl border border-white/5 p-3 flex items-start gap-3 flex-wrap" data-testid={`audience-item-${a.audience_id}`}>
                <button onClick={() => setSelected(a)} className="flex-1 text-left min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-white truncate">{a.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 font-bold">🔥 {a.hot_count}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 font-bold">🌤️ {a.warm_count}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#f7c935]/15 text-[#f7c935] font-bold">{eur(a.suggested_total_budget_eur)}/mois</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{a.description}</p>
                </button>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => exportCsv(a.audience_id)} className="text-xs px-2 py-1 rounded-lg bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 inline-flex items-center gap-1" data-testid={`audience-csv-${a.audience_id}`}>
                    <Download className="w-3 h-3" /> CSV
                  </button>
                  <button onClick={() => remove(a.audience_id)} className="text-xs px-2 py-1 rounded-lg bg-rose-500/10 text-rose-300 hover:bg-rose-500/20" data-testid={`audience-delete-${a.audience_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Brief details */}
      {selected && <AudienceBriefDetails brief={selected} onClose={() => setSelected(null)} onExport={() => exportCsv(selected.audience_id)} />}
    </div>
  );
}

function AudienceBriefDetails({ brief, onClose, onExport }) {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [showROI, setShowROI] = useState(false);
  const persona = brief.target_persona || {};
  return (
    <div className="bg-[#14101c] border border-[#f7c935]/30 rounded-2xl p-5 space-y-4 relative" data-testid="audience-brief-details">
      <button onClick={onClose} className="absolute top-3 right-3 text-slate-500 hover:text-white text-xl leading-none">×</button>

      <div>
        <h4 className="text-base font-bold text-white">📄 {brief.name}</h4>
        <p className="text-xs text-slate-400 mt-1">{brief.description}</p>
        <div className="mt-2 text-[10px] text-slate-500">
          {brief.audience_size} prospects · {brief.source === "fallback" ? "⚙️ heuristique" : `🤖 ${brief.model}`}
        </div>
      </div>

      {/* Persona */}
      {persona.title && (
        <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-wider text-violet-300 font-bold mb-1">Persona cible</div>
          <div className="text-sm font-bold text-white">{persona.title}</div>
          <p className="text-xs text-slate-300 mt-1">{persona.summary}</p>
          {persona.decision_drivers && (
            <div className="mt-3">
              <div className="text-[10px] text-emerald-400 font-bold uppercase mb-1">Motivations</div>
              <div className="flex flex-wrap gap-1">
                {persona.decision_drivers.map((d, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">✓ {d}</span>
                ))}
              </div>
            </div>
          )}
          {persona.objections && (
            <div className="mt-2">
              <div className="text-[10px] text-rose-400 font-bold uppercase mb-1">Objections probables</div>
              <div className="flex flex-wrap gap-1">
                {persona.objections.map((o, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300">⚠ {o}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Budget */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <div className="bg-[#0b0811] rounded-lg border border-white/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Budget mensuel</div>
          <div className="text-lg font-bold text-[#f7c935]">{eur(brief.suggested_total_budget_eur)}</div>
        </div>
        <div className="bg-[#0b0811] rounded-lg border border-white/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Budget jour</div>
          <div className="text-lg font-bold text-white">{eur(brief.suggested_daily_budget_eur)}</div>
        </div>
        <div className="bg-[#0b0811] rounded-lg border border-white/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">CPA cible</div>
          <div className="text-lg font-bold text-white">{eur(brief.expected_cpa_eur)}</div>
        </div>
        <div className="bg-[#0b0811] rounded-lg border border-white/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">ROAS</div>
          <div className="text-lg font-bold text-emerald-400">x{brief.expected_roas}</div>
        </div>
      </div>

      {/* Google Keywords */}
      {(brief.google_keywords || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-blue-400 font-bold mb-2 flex items-center gap-1.5">
            <Target className="w-3 h-3" /> Mots-clés Google Ads
          </div>
          <div className="flex flex-wrap gap-1.5">
            {brief.google_keywords.map((k, i) => (
              <span key={i} className="text-[11px] px-2 py-1 rounded-lg bg-blue-500/15 text-blue-300 border border-blue-500/20" data-testid={`audience-keyword-${i}`}>
                {k.keyword || k} <span className="text-blue-500/60 text-[9px]">[{k.match_type || "broad"}]</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Meta interests */}
      {(brief.meta_interests || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-violet-400 font-bold mb-2">📘 Intérêts Meta (Facebook/Instagram)</div>
          <div className="flex flex-wrap gap-1.5">
            {brief.meta_interests.map((i, n) => (
              <span key={n} className="text-[11px] px-2 py-1 rounded-lg bg-violet-500/15 text-violet-300 border border-violet-500/20">
                {i}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Headlines + Descriptions */}
      {(brief.headlines || []).length > 0 && (
        <div className="grid md:grid-cols-2 gap-3">
          <div className="bg-[#0b0811] rounded-xl border border-white/5 p-3">
            <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-2">✏️ Titres</div>
            <ul className="space-y-1">
              {brief.headlines.map((h, i) => (
                <li key={i} className="text-sm text-white border-l-2 border-amber-500/30 pl-2">{h}</li>
              ))}
            </ul>
          </div>
          {(brief.descriptions || []).length > 0 && (
            <div className="bg-[#0b0811] rounded-xl border border-white/5 p-3">
              <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-2">📝 Descriptions</div>
              <ul className="space-y-1">
                {brief.descriptions.map((d, i) => (
                  <li key={i} className="text-sm text-slate-300 border-l-2 border-amber-500/30 pl-2">{d}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Strategy */}
      {brief.campaign_strategy && (
        <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold mb-2 flex items-center gap-1.5">
            <Lightbulb className="w-3 h-3" /> Stratégie recommandée
          </div>
          <p className="text-sm text-slate-200 leading-relaxed">{brief.campaign_strategy}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/5">
        <Button onClick={onExport} className="bg-emerald-500 text-white hover:bg-emerald-600 font-bold text-xs" data-testid="brief-export-csv-btn">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Exporter pour Meta / Google Ads
        </Button>
        <Button onClick={() => setShowROI((v) => !v)} variant="outline" className="border-white/10 text-slate-300 text-xs" data-testid="brief-toggle-roi">
          <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> {showROI ? "Masquer" : "Voir"} le ROI réel
        </Button>
        <span className="text-[10px] text-slate-500 ml-auto">
          <TrendingUp className="w-3 h-3 inline mr-1" />
          ROAS attendu : <strong className="text-emerald-400">x{brief.expected_roas}</strong> · CA estimé : <strong className="text-[#f7c935]">{eur((brief.suggested_total_budget_eur || 0) * (brief.expected_roas || 1))}</strong>
        </span>
      </div>

      {/* ROI panel (collapsible) */}
      {showROI && <AudienceROIPanel api={api} audienceId={brief.audience_id} />}
    </div>
  );
}
